/* VictoryBurst — gold-coin particle explosion for battle wins.
 *
 * Emits ~28 coins from the screen center that arc outward in random
 * directions and fall under gravity. Pure framer-motion; no extra
 * dependencies. Renders only when `active` is true, and unmounts itself
 * after ~2.6s so the coins don't pin memory.
 *
 * Respects `prefers-reduced-motion` — falls back to a soft radial flash.
 */
import { useMemo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const COIN_COUNT = 28;
const DURATION_MS = 2600;

function useReducedMotion() {
  const [rm, setRm] = useState(false);
  useEffect(() => {
    const m = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!m) return;
    const on = () => setRm(m.matches);
    on();
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, []);
  return rm;
}

function buildCoins() {
  return Array.from({ length: COIN_COUNT }).map((_, i) => {
    const angle = (Math.PI * -1) + Math.random() * Math.PI; // upward hemisphere
    const power = 180 + Math.random() * 240;                // px thrust
    return {
      id: i,
      dx: Math.cos(angle) * power,
      dy: Math.sin(angle) * power,     // negative → up
      spin: (Math.random() * 720 - 360),
      size: 14 + Math.round(Math.random() * 12),
      delay: Math.random() * 0.18,
    };
  });
}

/* Individual coin — two-phase motion: thrust up/out, then fall + fade. */
function Coin({ c }) {
  const dur = DURATION_MS / 1000;
  return (
    <motion.div
      initial={{ x: 0, y: 0, rotate: 0, opacity: 0, scale: 0.5 }}
      animate={{
        // Two keyframes: peak (thrust) → resting (gravity + off-screen)
        x: [0, c.dx * 0.9, c.dx],
        y: [0, c.dy, c.dy + 640],
        rotate: [0, c.spin * 0.5, c.spin],
        opacity: [0, 1, 1, 0],
        scale: [0.5, 1, 1, 0.9],
      }}
      transition={{
        duration: dur,
        delay: c.delay,
        times: [0, 0.15, 0.55, 1],
        ease: ['easeOut', 'easeIn', 'easeIn'],
      }}
      className="absolute left-1/2 top-1/2 pointer-events-none"
      style={{ width: c.size, height: c.size, marginLeft: -c.size / 2, marginTop: -c.size / 2 }}
      aria-hidden
    >
      <div
        className="w-full h-full rounded-full"
        style={{
          background:
            'radial-gradient(circle at 32% 28%, #fff6c1 0%, #fde68a 22%, #f7c948 45%, #b8860b 78%, #6b4a08 100%)',
          boxShadow:
            '0 0 12px rgba(253, 224, 71, 0.85), 0 0 24px rgba(253, 224, 71, 0.35), inset 0 -2px 3px rgba(0,0,0,0.35), inset 0 2px 2px rgba(255,255,255,0.55)',
          border: '1px solid rgba(120, 82, 8, 0.65)',
        }}
      />
    </motion.div>
  );
}

/* Static "WIN" flash ring behind the coins so the moment lands. */
function VictoryRing() {
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: [0, 1.15, 1.35], opacity: [0.9, 0.4, 0] }}
      transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1] }}
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
      style={{
        width: 320,
        height: 320,
        borderRadius: '50%',
        background:
          'radial-gradient(circle, rgba(253, 224, 71, 0.55) 0%, rgba(253, 224, 71, 0.18) 32%, transparent 68%)',
        filter: 'blur(0.5px)',
      }}
      aria-hidden
    />
  );
}

export default function VictoryBurst({ active }) {
  const reduced = useReducedMotion();
  // Re-randomise the coin field each time the burst activates so a
  // rematch on the same route mount doesn't replay identical trajectories.
  const [burstKey, setBurstKey] = useState(0);
  useEffect(() => { if (active) setBurstKey((k) => k + 1); }, [active]);
  const coins = useMemo(buildCoins, [burstKey]);
  const [alive, setAlive] = useState(true);

  useEffect(() => {
    if (!active) return;
    setAlive(true);
    const t = setTimeout(() => setAlive(false), DURATION_MS + 220);
    return () => clearTimeout(t);
  }, [active, burstKey]);

  return (
    <AnimatePresence>
      {active && alive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="pointer-events-none absolute inset-0 z-[60] overflow-hidden"
          data-testid="victory-burst"
        >
          {/* Soft golden vignette flash under the coins */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: reduced ? 0.45 : [0.9, 0.35, 0] }}
            transition={{ duration: reduced ? 0.6 : 1.2, ease: 'easeOut' }}
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at 50% 45%, rgba(253, 224, 71, 0.35) 0%, rgba(253, 224, 71, 0.10) 30%, transparent 62%)',
            }}
            aria-hidden
          />
          <VictoryRing />
          {!reduced && coins.map((c) => <Coin key={c.id} c={c} />)}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
